// The three HTTP calls Path A needs, each injectable so the gate is testable without a
// browser, a network or a Connect.
//
// Authority: /ceph/projects/coordination/sdk-demos/SDK-DEMO-LOGIN-STANDARD-RECIPE.md §1.
//
// ---------------------------------------------------------------------------
// WHAT IS HAND-ROLLED HERE AND WHAT IS NOT
// ---------------------------------------------------------------------------
// The recipe bans hand-rolled auth and says to use the shipped SDK. So the BRIDGE —
// the one step with real cryptographic state — is `bridgeSession` from
// `@anshinus/auth/client`, not a hand-written POST. That helper already sends
// `credentials: 'include'`, already forwards the UA correctly from a browser context,
// and already knows the `${apiUrl}/api/auth/session/bridge` path.
//
// `/session/validate` and `/me` are plain credentialed GETs. The SDK's own client cannot
// make them from a browser: `AnshinAuthClient.validateSession(sessionCookie: string)`
// takes the cookie VALUE as an argument, and the per-app cookie is `HttpOnly`, so
// JavaScript cannot read it. That API is for a server-side host that receives the cookie
// header. In a browser the cookie is attached automatically and never visible, so a
// credentialed `fetch` is the only available mechanism — not a preference.
//
// This is the first thing that did not fit a static SPA, and it is a property of the
// shipped client, not of this demo.

import { bridgeSession } from '@anshinus/auth/client';
import type { MeDto, SessionValidateDto } from './dto';
import { resolveSession, type ResolvedSession } from './dto';
import { authApiUrl, type ConnectConfig } from './gate';

/** What a liveness check against Connect concluded. No variant implies access. */
export type SessionProbe =
  | { kind: 'session'; session: ResolvedSession }
  /** Connect answered, and said no. 401, revoked, or a non-conforming DTO. */
  | { kind: 'no-session'; detail: string }
  /** No answer at all — CORS, DNS, TLS, offline. Distinct from "answered no". */
  | { kind: 'unreachable'; detail: string };

export interface AuthFetchers {
  /** Injected for tests. Defaults to `globalThis.fetch`. */
  fetchImpl?: typeof fetch;
  /** Injected for tests. Defaults to the SDK's `bridgeSession`. */
  bridgeImpl?: typeof bridgeSession;
}

/**
 * Ask Connect who the user is, using the cookie the browser holds.
 *
 * BOTH endpoints are called, because Shape A splits the answer across them:
 * `is_platform_admin` is only on `/session/validate` and `tenant` is only on `/me`.
 * Calling one and defaulting the other's fields is the silent-failure the DTO module
 * refuses.
 *
 * `/session/validate` is called FIRST and a non-200 short-circuits. It is the endpoint
 * that reports `revoked`, so a session Connect has killed must not get as far as `/me`
 * — and `/me` for a dead session is a request with a predictable answer and an audit row.
 *
 * NEVER THROWS, and never returns a session it could not fully resolve. A caller that
 * gets anything but `kind: 'session'` has no identity and therefore no product access.
 */
export async function probeSession(
  config: ConnectConfig,
  deps: AuthFetchers = {},
): Promise<SessionProbe> {
  const doFetch = deps.fetchImpl ?? fetch;

  let validateRes: Response;
  try {
    validateRes = await doFetch(authApiUrl(config, '/session/validate'), {
      method: 'GET',
      headers: { accept: 'application/json' },
      // The per-app cookie IS the credential. Without this the call is anonymous and
      // Connect answers 401 — identical to "not logged in", so the bug would look
      // exactly like the normal unauthenticated case.
      credentials: 'include',
    });
  } catch (err) {
    return { kind: 'unreachable', detail: message(err) };
  }

  if (validateRes.status === 401 || validateRes.status === 403) {
    return { kind: 'no-session', detail: `session/validate → ${validateRes.status}` };
  }
  if (!validateRes.ok) {
    // A 5xx is NOT "no session" — it is Connect failing. Reported as unreachable so the
    // page shows an error rather than bouncing the user into a login loop against a
    // Connect that cannot answer.
    return { kind: 'unreachable', detail: `session/validate → ${validateRes.status}` };
  }

  let validate: SessionValidateDto;
  try {
    validate = (await validateRes.json()) as SessionValidateDto;
  } catch (err) {
    return { kind: 'unreachable', detail: `session/validate body is not JSON: ${message(err)}` };
  }

  let meRes: Response;
  try {
    meRes = await doFetch(authApiUrl(config, '/me'), {
      method: 'GET',
      headers: { accept: 'application/json' },
      credentials: 'include',
    });
  } catch (err) {
    return { kind: 'unreachable', detail: message(err) };
  }
  if (meRes.status === 401 || meRes.status === 403) {
    return { kind: 'no-session', detail: `me → ${meRes.status}` };
  }
  if (!meRes.ok) {
    return { kind: 'unreachable', detail: `me → ${meRes.status}` };
  }

  let me: MeDto;
  try {
    me = (await meRes.json()) as MeDto;
  } catch (err) {
    return { kind: 'unreachable', detail: `me body is not JSON: ${message(err)}` };
  }

  try {
    return { kind: 'session', session: resolveSession(validate, me) };
  } catch (err) {
    // A DTO that does not satisfy Shape A is NOT a session. Reported as no-session so
    // the page denies rather than proceeding with a partial identity — the tenant is the
    // field most likely to be missing, and an absent tenant is precisely what must not
    // be defaulted.
    return { kind: 'no-session', detail: message(err) };
  }
}

export type BridgeOutcome =
  | { kind: 'bridged' }
  | { kind: 'failed'; detail: string };

/**
 * Swap the `exchange_code` for the per-app session cookie.
 *
 * Called BROWSER-SIDE on purpose. The recipe §1 calls the `Set-Cookie` header "THE
 * critical mechanic": the session is in the header, not the body, and a caller that
 * reads `{"ok":true}` and drops the header establishes nothing. From the browser the
 * header lands directly on `document`'s cookie jar with no forwarding to get wrong —
 * which is why the reference host's ~15 lines of `Set-Cookie` forwarding are a
 * SERVER-side concern this SPA does not have.
 *
 * `bridgeApiUrl` is SEPARATE from `authBasePath`, and that separation is load-bearing.
 * The exchange code is bound to `(session_id, client_ip, sha256(user_agent))`. A
 * reverse-proxied POST reaches Connect from the DEMO POD's IP, not the browser's, so the
 * IP binding would not match unless Connect honours a forwarded-for header — which this
 * session could not verify. Keeping the two configurable independently means the bridge
 * can go direct while `/validate` and `/me` go through the same-origin proxy, without a
 * code change.
 */
export async function runBridge(
  config: ConnectConfig,
  exchangeCode: string,
  bridgeApiUrl: string,
  deps: AuthFetchers = {},
): Promise<BridgeOutcome> {
  const doBridge = deps.bridgeImpl ?? bridgeSession;
  try {
    const result = await doBridge(exchangeCode, {
      apiUrl: bridgeApiUrl,
      app: config.appKey,
      ...(deps.fetchImpl ? { fetchImpl: deps.fetchImpl } : {}),
    });
    // The helper reports success/failure; the SESSION itself is proved by re-probing
    // afterwards, never by trusting this body. `{"ok":true}` means the server accepted
    // the code, not that the browser now holds a usable cookie — and the whole point of
    // the Set-Cookie warning is that those two can differ.
    if (result && (result as { ok?: boolean }).ok === false) {
      return { kind: 'failed', detail: describeBridge(result) };
    }
    return { kind: 'bridged' };
  } catch (err) {
    return { kind: 'failed', detail: message(err) };
  }
}

/**
 * Describe a failed bridge from the helper's OWN result shape.
 *
 * `{ ok, status, error, message }` — read off `session-bridge.js`, not guessed. An
 * earlier version of this also looked for `errorCode`, a field the helper never sets, and
 * the unit test asserting it was passing against a fiction: the test's fixture invented
 * the same field, so the two agreed with each other and with nothing that ships.
 *
 * The helper resolves `error` from Connect's standardised 4xx body
 * `{detail: {error: "<code>", message: "..."}}` (falling back to a top-level `error`,
 * then to `http_<status>`), so Connect's own code DOES reach here when Connect sends one.
 */
function describeBridge(result: unknown): string {
  const r = (result ?? {}) as Record<string, unknown>;
  const parts = [r.error, r.message].filter(
    (v): v is string => typeof v === 'string' && v !== '',
  );
  if (typeof r.status === 'number' && r.status !== 0) parts.push(`HTTP ${r.status}`);
  return parts.length ? parts.join(': ') : 'bridge reported ok=false with no detail';
}

function message(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
